import { useState } from 'react';

import { Composer } from '@/modules/assistant/parts/composer';
import { MessageList } from '@/modules/assistant/parts/message-list';
import { PanelNotices } from '@/modules/assistant/parts/panel-notices';
import { PanelToolbar } from '@/modules/assistant/parts/panel-toolbar';
import { SuggestionList } from '@/modules/assistant/parts/suggestion-list';
import { ThreadList } from '@/modules/assistant/parts/thread-list';
import {
  useAssistantContext,
  useAssistantMessages,
  useAssistantSend,
  useAssistantThreads,
  useSelectAssistantThread,
  useSelectedAssistantThreadId,
} from '@/modules/assistant/hooks';
import { describeOptionalFailure } from '@/modules/assistant/shared/assistant-error';
import { assistantBodyClasses } from '@/modules/assistant/parts/styles';
import type { AssistantScope } from '@/modules/assistant/services/assistant-scope';

export type AssistantPanelContentProps = {
  /** The route the person is on. It selects the skill and the suggestions. */
  route: string;
  /** Which family of the assistant to ask. */
  scope: AssistantScope;
};

/**
 * The body of the assistant panel.
 *
 * ---------------------------------------------------------------------------
 * THE ROUTE IS THE ONLY INPUT
 * ---------------------------------------------------------------------------
 * Everything the panel shows follows from the route: the route picks the family
 * (tenant or platform), the route picks the skill, the skill carries the
 * suggestions, and the suggestions are offered on THIS page. There is no second
 * place where a page decides what the assistant may talk about.
 *
 * ---------------------------------------------------------------------------
 * NOTHING IS APPLIED HERE, ONLY PUBLISHED
 * ---------------------------------------------------------------------------
 * The panel shows a draft for review and publishes it when the person chooses to.
 * It never writes to a form and never writes to the API.
 */
export function AssistantPanelContent({ route, scope }: AssistantPanelContentProps) {
  const [composerText, setComposerText] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  const context = useAssistantContext(scope, route);
  const threads = useAssistantThreads(scope);
  const threadId = useSelectedAssistantThreadId(scope);
  const messages = useAssistantMessages(scope, threadId);
  const selectThread = useSelectAssistantThread();
  const { send, isSending, error: sendError } = useAssistantSend(scope, route, threadId);

  const handleSend = async () => {
    const text = composerText.trim();
    if (text === '') return;

    try {
      await send(text);
      setComposerText('');
    } catch {
      // The failure is carried by the hook and shown under the composer.
    }
  };

  return (
    <div className={assistantBodyClasses}>
      <PanelToolbar
        historyOpen={historyOpen}
        onToggleHistory={() => setHistoryOpen((open) => !open)}
        canStartNew={threadId !== null}
        onNewConversation={() => selectThread(null, scope)}
      />

      {historyOpen ? (
        <ThreadList
          threads={threads.data ?? []}
          selectedThreadId={threadId}
          onSelect={(id) => selectThread(id, scope)}
        />
      ) : null}

      <PanelNotices
        contextError={context.error}
        threadsError={threads.error}
        sendError={sendError}
      />

      <SuggestionList
        suggestions={context.data?.skill?.suggestions ?? []}
        onPick={setComposerText}
      />

      <MessageList
        messages={messages.data ?? []}
        agentName={context.data?.agent.name ?? 'Assistant'}
        isLoading={messages.isPending && threadId !== null}
        error={describeOptionalFailure(messages.error)}
        onRetry={() => void messages.refetch()}
      />

      <Composer
        value={composerText}
        onChange={setComposerText}
        onSend={() => void handleSend()}
        isSending={isSending}
      />
    </div>
  );
}
